import toast from 'react-hot-toast';
import FormSearch from './form-search';
import ShipLoading from '@/components/ui/loader/ShipLoading';
import { useQueryGetDetailVesselReport, getDetailVesselReportExport } from '@/api/queries/vessels';
import { Fragment, useEffect, useState } from 'react';

import { Daily, Summary, VesselHistory, VesselHistoryMeteorology } from '@/types/api';
import { Transition, Combobox } from '@headlessui/react';
import LineChart from '@/components/ui/charts/LineChart';
import Tabs from '@/components/ui/tabs';
import DatatableWithTheme from '@/components/ui/datatableWIthTheme';

import WheelIcon from '@/assets/icons/wheel.svg';
import CheckIcon from '@/assets/icons/check.svg';
import CaretDownIcon from '@/assets/icons/caret-down.svg';
import { 
  FileBarChart2, TableProperties, Gauge, Calendar, BarChart3, Cloud,
  Compass, Thermometer, Wind, Droplets, Eye, MapPin, Navigation, Waves, Anchor, Download, Map
} from 'lucide-react';
import SimpleRouteMap from './SimpleRouteMap';
import { cn } from '@/utils';

type filterForm = {
  vessel: string;
  startDate: string;
  endDate: string;
};

type WeatherType = {
  temp: string;
  temp_min: string;
  temp_max: string;
  humidity: string;
  sea_level: string;
  pressure: string;
  visibility: string;
  wind_deg: string;
  wind_gust: string;
  wind_speed: string;
  clouds: string;
};

interface WeatherEntry {
  title: string;
  unit?: string;
}

const TABS = [
  { id: 'timeline', label: 'Timeline', icon: TableProperties },
  { id: 'summary', label: 'Summary', icon: Gauge },
  { id: 'daily', label: 'Daily', icon: Calendar },
  { id: 'chart', label: 'Chart', icon: BarChart3 },
  { id: 'map', label: 'Map Route', icon: Map },
] as const;

export default function DetailReportHistory() {
  const [activeTab, setActiveTab] = useState<string>('timeline');
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [filter, setFilter] = useState<filterForm>({
    vessel: '',
    startDate: '',
    endDate: '',
  });

  const [timelineChart, setTimelineChart] = useState<any>({
    labels: [],
    datasets: [],
  });

  const listWeather: Record<keyof WeatherType, WeatherEntry> = {
    temp: {
      title: 'Temperature',
      unit: '°C',
    },
    temp_min: {
      title: 'Temperature Min',
      unit: '°C',
    },
    temp_max: {
      title: 'Temperature Max',
      unit: '°C',
    },
    humidity: {
      title: 'Humidity',
      unit: '%',
    },
    sea_level: {
      title: 'Sea Level',
      unit: 'hPa',
    },
    pressure: {
      title: 'Pressure',
      unit: 'hPa',
    },
    visibility: {
      title: 'Visibility',
      unit: 'm',
    },
    wind_deg: {
      title: 'Wind Bearing',
      unit: '°',
    },
    wind_gust: {
      title: 'Wind Gust',
      unit: 'knots',
    },
    wind_speed: {
      title: 'Wind Speed',
      unit: 'knots',
    },
    clouds: {
      title: 'Cloud Cover',
      unit: '%',
    },
  };
  const [meteorologyLineChartData, setMeteorologyLineChartData] = useState<{
    [key: string]: any;
  }>([]);
  const [selectedWeatherChart, setSelectedWeatherChart] = useState<keyof WeatherType | null>(null);

  const [meteorologyList, setMeteorologyList] = useState<VesselHistoryMeteorology[]>([]);

  const [weatherChart, setWeatherChart] = useState<{
    labels: any[];
    datasets: any[]; // Corrected the property name from "datesets" to "datasets"
  }>({
    labels: [],
    datasets: [],
  });

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const {
    error: errorDetailVessel,
    isError: isErrorDetailVessel,
    data: dataDetailVessel,
    isLoading: isLoadingDetailVessel,
    isFetching: isFetchingDetailVessel,
    refetch,
  } = useQueryGetDetailVesselReport(
    filter.vessel,
    filter.startDate,
    filter.endDate,
    true,
    'history',
  );

  const onSearch = (value: filterForm) => {
    if (value.vessel === '' || value.vessel === null) {
      toast.error('Vessel is required', {
        position: 'top-center',
      });
      return;
    }

    if (value.startDate === '') {
      toast.error('Start Date is required', {
        position: 'top-center',
      });
      return;
    }

    if (value.endDate === '') {
      toast.error('End Date is required', {
        position: 'top-center',
      });
      return;
    }

    setFilter(value);
  };

  useEffect(() => {
    if (
      filter.vessel !== '' &&
      filter.vessel !== null &&
      filter.startDate !== '' &&
      filter.vessel !== null &&
      filter.endDate !== '' &&
      filter.endDate !== null
    ) {
      refetch();
    }
  }, [filter]);

  const columns = [
    {
      name: 'Date',
      selector: (row: VesselHistory) => row.receive_date,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Latitude',
      selector: (row: VesselHistory) => row.latitude,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Longitude',
      selector: (row: VesselHistory) => row.longitude,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Heading',
      selector: (row: VesselHistory) => row.heading,
      sortable: true,
      width: '100px'
    },
    {
      name: 'Speed',
      selector: (row: VesselHistory) => row.speed,
      sortable: true,
      width: '100px'
    },
    {
      name: 'Distance Moving',
      selector: (row: VesselHistory) => row.distance,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Proximity',
      cell: (row: VesselHistory) => {
        return (
          <p
            data-tag="allowRowEvents"
            style={{
              whiteSpace: 'wrap',
              maxWidth: '800px',
            }}
          >
            {row.proximity}
          </p>
        );
      },
      maxwidth: '600px'
    }
  ];

  const columnsMeteorology = [
    {
      name: 'Receive Date',
      selector: (row: VesselHistoryMeteorology) => row.receive_date,
      sortable: true,
      width: '150px'
    },

    {
      name: 'Summary Weather',
      cell: (row: VesselHistoryMeteorology) => {
        return <p className="flex flex-col items-center justify-center">{row.summary} </p>;
      },
      width: '150px'
    },
    {
      name: 'Icon Weather',
      cell: (row: VesselHistoryMeteorology) => {
        return (
          <img
            className="w-10 h-10 ml-4"
            src={`http://openweathermap.org/img/w/${row?.icon}.png`}
          />
        );
      },
      width: '150px'
    },
    {
      name: 'Temperature',
      selector: (row: VesselHistoryMeteorology) => `${row.temp} °C`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Temperature Min',
      selector: (row: VesselHistoryMeteorology) => `${row.temp_min} °C`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Temperatur Max',
      selector: (row: VesselHistoryMeteorology) => `${row.temp_max} °C`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Humidity',
      selector: (row: VesselHistoryMeteorology) => `${row.humidity} %`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Sea Level',
      selector: (row: VesselHistoryMeteorology) => `${row.sea_level} hPa`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Preasure',
      selector: (row: VesselHistoryMeteorology) => `${row.pressure} hPa`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Visibility',
      selector: (row: VesselHistoryMeteorology) => `${row.visibility} m`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Wind Bearing',
      selector: (row: VesselHistoryMeteorology) => `${row.wind_deg} °`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Wind Gust',
      selector: (row: VesselHistoryMeteorology) => `${row.wind_gust} knots`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Wind Speed',
      selector: (row: VesselHistoryMeteorology) => `${row.wind_speed} knots`,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Cloud Cover',
      selector: (row: VesselHistoryMeteorology) => `${row.clouds} %`,
      sortable: true,
      width: '150px'
    },
  ];

  const columnsSummary = [
    {
      name: 'Average Speed',
      selector: (row: Summary) => `${row.average_speed} Knots`,
      sortable: true,
      width: windowWidth <= 768 ? '150px' : 'auto'
    },
    {
      name: 'Max Speed',
      selector: (row: Summary) => `${row.max_speed} Knots`,
      sortable: true,
      width: windowWidth <= 768 ? '150px' : 'auto'
    },
    {
      name: 'Total Distance',
      selector: (row: Summary) => `${row.total_distance} NM`,
      sortable: true,
      width: windowWidth <= 768 ? '150px' : 'auto'
    },
  ];

  const columnsDaily = [
    {
      name: 'Date',
      selector: (row: Daily) => row.date,
      sortable: true,
      width: windowWidth <= 768 ? '100px' : 'auto'
    },
    {
      name: 'Avg Speed',
      selector: (row: Daily) => `${row.speed} Knots`,
      sortable: true,
      width: windowWidth <= 768 ? '150px' : 'auto'
    },
    {
      name: 'Sub Distance',
      selector: (row: Daily) => `${row.distance} NM`,
      sortable: true,
      width: windowWidth <= 768 ? '150px' : 'auto'
    },
    {
      name: 'Total',
      selector: (row: Daily) => row.count,
      sortable: true,
      width: windowWidth <= 768 ? '100px' : 'auto'
    },
  ];

  useEffect(() => {
    if (dataDetailVessel?.timeline) {
      const speed = dataDetailVessel?.timeline.map((item) => item.speed);
      const dataset = {
        labels: dataDetailVessel?.timeline.map((item) => item.receive_date),
        datasets: [
          {
            label: 'Speed',
            data: speed,
            fill: false,
            backgroundColor: 'rgb(255, 99, 132)',
            borderColor: 'rgba(255, 99, 132, 0.2)',
          },
        ],
      };

      setTimelineChart(dataset);
      setWeatherChart({
        labels: dataset?.labels,
        datasets: [],
      });

      /**
       * Set meteorology list
       */

      const meteorology = dataDetailVessel?.timeline?.map((item) => {
        return {
          ...item.vessel_history_meteorology,
          receive_date: item.receive_date,
        };
      });

      setMeteorologyList(meteorology);

      let meteorologyChartDataTemp: any = {};

      meteorology.forEach((val) => {
        Object.keys(listWeather).forEach((key) => {
          if (meteorologyChartDataTemp[key]) {
            // @ts-ignore
            meteorologyChartDataTemp[key].push(val[key]);
          } else {
            // @ts-ignore
            meteorologyChartDataTemp[key] = [val[key]];
          }
        });
      });

      setMeteorologyLineChartData(meteorologyChartDataTemp);
      setSelectedWeatherChart('temp');
    }
  }, [dataDetailVessel]);

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      setExportProgress(0);
      const interval = setInterval(() => {
        setExportProgress((prev) => (prev >= 90 ? 90 : prev + 15));
      }, 500);

      const response = await getDetailVesselReportExport(
        filter.vessel,
        filter.startDate,
        filter.endDate,
        true,
        'history',
        'pdf',
        'false',
      );

      clearInterval(interval);
      setExportProgress(100);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `report-${filter.vessel}-${filter.startDate}-${filter.endDate}.pdf`,
      ); //or any other extension
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setTimeout(() => {
        setExporting(false);
        setExportProgress(0);
      }, 500);
    }
  };

  const handleExportXLSX = async () => {
    try {
      setExporting(true);
      setExportProgress(0);
      const interval = setInterval(() => {
        setExportProgress((prev) => (prev >= 90 ? 90 : prev + 15));
      }, 500);

      const response = await getDetailVesselReportExport(
        filter.vessel,
        filter.startDate,
        filter.endDate,
        true,
        'history',
        'xlsx',
        'false',
      );

      clearInterval(interval);
      setExportProgress(100);

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${filter.vessel}-${filter.startDate}-${filter.endDate}.xlsx`;
      link.click();
    } catch (error) {
      console.error('Error exporting xlsx:', error);
    } finally {
      setTimeout(() => {
        setExporting(false);
        setExportProgress(0);
      }, 500);
    }
  };

  useEffect(() => {
    if (selectedWeatherChart) {
      const dataset = {
        labels: weatherChart.labels,
        datasets: [
          {
            label: `${listWeather[selectedWeatherChart]?.title} ${listWeather[selectedWeatherChart]?.unit}`,
            data: meteorologyLineChartData[selectedWeatherChart], // Explicitly specify the type of the index expression as 'number'
            fill: false,
            backgroundColor: 'rgb(255, 99, 132)',
            borderColor: 'rgba(255, 99, 132, 0.2)',
          },
        ],
      };

      setWeatherChart(dataset);
    }
  }, [selectedWeatherChart]);
  return (
    <div className="space-y-6 relative">
      {isFetchingDetailVessel && (
        <ShipLoading label="Loading data kapal..." fullScreen={true} />
      )}
      {exporting && (
        <ShipLoading 
          label="Mengekspor laporan..." 
          showProgress={true} 
          progress={exportProgress} 
          fullScreen={true} 
        />
      )}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-sm">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,.45), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,.25), transparent 45%)",
          }}
        />
        <div className="relative p-4 sm:p-6 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex sm:flex-wrap sm:justify-between">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <div className="h-10 w-10 sm:h-12 sm:w-12 shrink-0 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/25">
              <FileBarChart2 className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/70 font-semibold mb-1">
                Fleet intelligence
              </div>
              <h1 className="text-lg sm:text-2xl font-bold tracking-tight truncate">
                Vessel History Report
              </h1>
              <p className="hidden sm:block text-sm text-white/80 mt-1 max-w-xl">
                Per-vessel telemetry, voyage summary, daily breakdown & meteorology data over time.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={handleExportPDF}
              disabled={exporting || !((dataDetailVessel?.timeline?.length ?? 0) > 0)}
              aria-label="Export PDF"
              className="h-9 w-9 sm:h-10 sm:w-auto sm:px-4 shrink-0 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur ring-1 ring-white/25 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            >
              {exporting ? <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="hidden sm:inline">Export PDF</span>
            </button>
            <button
              onClick={handleExportXLSX}
              disabled={exporting || !((dataDetailVessel?.timeline?.length ?? 0) > 0)}
              aria-label="Export Excel"
              className="h-9 w-9 sm:h-10 sm:w-auto sm:px-4 shrink-0 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur ring-1 ring-white/25 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            >
              {exporting ? <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="hidden sm:inline">Export Excel</span>
            </button>
          </div>
        </div>
      </div>

      <FormSearch onSearch={onSearch} type="history" />

      <div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 sm:inline-flex sm:gap-1">
          {TABS.map((t) => {
            const active = activeTab === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`relative h-9 px-2 sm:px-3.5 rounded-lg inline-flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  active
                    ? 'bg-white dark:bg-gray-900 text-brand-500 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm mt-3">
          <div className="flex items-start justify-between p-5 pb-3">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white tracking-tight">
                {TABS.find((t) => t.id === activeTab)?.label}
                {filter.vessel ? ` — ${filter.vessel}` : ''}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {filter.startDate && filter.endDate 
                  ? `${filter.startDate} → ${filter.endDate}` 
                  : "Run a search to view records"}
              </p>
            </div>
          </div>
          
          <div className="px-5 pb-5 overflow-x-auto">
            {activeTab === 'timeline' && (
              <>
                <TimelineTable rows={dataDetailVessel?.timeline || []} />
                <TimelineCards rows={dataDetailVessel?.timeline || []} />


              </>
            )}

            {activeTab === 'summary' && (
              <SummaryView data={{
                timeline: dataDetailVessel?.timeline || [],
                summary: dataDetailVessel?.summary || [],
                daily: dataDetailVessel?.daily || []
              }} />
            )}

            {activeTab === 'daily' && (
              <>
                <DailyTable rows={dataDetailVessel?.daily || []} />
                <DailyCards rows={dataDetailVessel?.daily || []} />
              </>
            )}

            {activeTab === 'chart' && (
              <ChartView rows={dataDetailVessel?.timeline || []} />
            )}

            {activeTab === 'map' && (
              <SimpleRouteMap
                routePoints={(dataDetailVessel?.timeline || [])
                  .map((t: any) => [parseFloat(t.latitude), parseFloat(t.longitude)])
                  .filter(([lat, lon]: any) => !isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0))}
                vesselName={filter.vessel}
                startDate={filter.startDate}
                endDate={filter.endDate}
                height="540px"
              />
            )}


          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- UI Helpers from Fleetflow ----------

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "py-2 pr-3 font-semibold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide",
        className
      )}
    >
      {children}
    </th>
  );
}

function fmtCoord(v: string | number) {
  if (!v) return "0.00000";
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return n.toFixed(5);
}

function TimelineTable({ rows }: { rows: any[] }) {
  const columns = [
    {
      name: 'Date',
      selector: (row: any) => row.receive_date,
      sortable: true,
      cell: (row: any) => (
        <div className="whitespace-nowrap py-3">
          <div className="font-medium text-gray-900 dark:text-white">{row.receive_date?.slice(0, 10)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{row.receive_date?.slice(11)}</div>
        </div>
      )
    },
    {
      name: 'Position',
      selector: (row: any) => row.latitude,
      sortable: true,
      cell: (row: any) => (
        <div className="tabular-nums whitespace-nowrap">
          <div className="text-xs text-gray-900 dark:text-white">{fmtCoord(row.latitude)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{fmtCoord(row.longitude)}</div>
        </div>
      )
    },
    {
      name: 'Heading',
      selector: (row: any) => parseFloat(row.heading || "0"),
      sortable: true,
      cell: (row: any) => {
        const heading = parseFloat(row.heading || "0");
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white text-xs">
            <Compass
              className="h-3.5 w-3.5 text-brand-500"
              style={{ transform: `rotate(${heading}deg)` }}
            />{" "}
            {heading.toFixed(0)}°
          </span>
        );
      }
    },
    {
      name: 'Speed',
      selector: (row: any) => parseFloat(row.speed || "0"),
      sortable: true,
      cell: (row: any) => <SpeedBar value={parseFloat(row.speed || "0")} />
    },
    {
      name: 'Distance',
      selector: (row: any) => parseFloat(row.distance || "0"),
      sortable: true,
      cell: (row: any) => (
        <div className="tabular-nums text-gray-900 dark:text-white">
          {parseFloat(row.distance || "0").toFixed(2)} <span className="text-xs text-gray-500 dark:text-gray-400">nm</span>
        </div>
      )
    },
    {
      name: 'Weather',
      selector: (row: any) => row.vessel_history_meteorology?.summary || "",
      sortable: true,
      cell: (row: any) => {
        const m = row.vessel_history_meteorology;
        return m ? (
          <div className="flex items-center gap-1.5 text-xs">
            <Cloud className="h-3.5 w-3.5 text-blue-500 shrink-0" />
            <span className="capitalize text-gray-900 dark:text-gray-200">{m.summary}</span>
            <span className="text-gray-500 dark:text-gray-400">· {parseFloat(m.clouds || "0").toFixed(0)}%</span>
          </div>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
        );
      }
    },
    {
      name: 'Temp',
      selector: (row: any) => parseFloat(row.vessel_history_meteorology?.temp || "0"),
      sortable: true,
      cell: (row: any) => {
        const m = row.vessel_history_meteorology;
        return m ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-900 dark:text-white tabular-nums">
            <Thermometer className="h-3.5 w-3.5 text-orange-500" />
            {parseFloat(m.temp || "0").toFixed(1)}°C
          </span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
        );
      }
    },
    {
      name: 'Wind',
      selector: (row: any) => parseFloat(row.vessel_history_meteorology?.wind_speed || "0"),
      sortable: true,
      cell: (row: any) => {
        const m = row.vessel_history_meteorology;
        return m ? (
          <div className="text-xs tabular-nums">
            <div className="inline-flex items-center gap-1.5 text-gray-900 dark:text-white">
              <Wind className="h-3.5 w-3.5 text-blue-500" /> {parseFloat(m.wind_speed || "0").toFixed(1)} m/s
            </div>
            <div className="text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5 ml-0.5 mt-0.5">
              <Droplets className="h-3 w-3" /> {m.humidity}%
              <Eye className="h-3 w-3 ml-1" /> {(parseFloat(m.visibility || "0") / 1000).toFixed(0)}km
            </div>
          </div>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
        );
      }
    },
    {
      name: 'Proximity',
      selector: (row: any) => row.proximity,
      sortable: true,
      cell: (row: any) => (
        <div className="flex items-start gap-1.5 text-gray-500 dark:text-gray-400 min-w-[200px] whitespace-normal">
          <MapPin className="h-3.5 w-3.5 mt-0.5 text-brand-500/70 shrink-0" />
          <div>
            <div className="text-gray-900 dark:text-gray-200 leading-snug">{row.proximity}</div>
            {row.proximity_distance != null && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {row.proximity_distance.toFixed(0)} m · {row.proximity_direction?.trim()}
              </div>
            )}
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="hidden md:block [&_.rdt_TableCell]:!px-2 [&_.rdt_TableCol]:!px-2">
      <DatatableWithTheme
        columns={columns}
        data={rows}
        pagination
        paginationPerPage={10}
      />
    </div>
  );
}

function SpeedBar({ value }: { value: number }) {
  const pct = Math.min(100, (value / 15) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium tabular-nums w-14 text-right text-gray-900 dark:text-white">{value.toFixed(2)} kn</span>
    </div>
  );
}

function SummaryView({ data }: { data: any }) {
  const s = data.summary[0] ?? { average_speed: "0", max_speed: "0", total_distance: "0" };
  const totalPoints = data.timeline.length;
  const moving = data.daily.reduce((a: number, d: any) => a + (d.countNotStopped || 0), 0);
  const stops = data.daily.reduce((a: number, d: any) => a + ((d.count || 0) - (d.countNotStopped || 0)), 0);
  const days = data.daily.length;

  const stats = [
    { label: "Total distance", value: parseFloat(s.total_distance).toFixed(2), unit: "nm", icon: Navigation, tone: "primary" },
    { label: "Avg speed", value: parseFloat(s.average_speed).toFixed(2), unit: "kn", icon: Gauge, tone: "info" },
    { label: "Max speed", value: parseFloat(s.max_speed).toFixed(2), unit: "kn", icon: Waves, tone: "success" },
    { label: "Stops / idle", value: `${stops}`, unit: "events", icon: Anchor, tone: "warning" },
    { label: "Records", value: `${totalPoints}`, unit: "points", icon: TableProperties, tone: "primary" },
    { label: "Moving samples", value: `${moving}`, unit: `/ ${totalPoints}`, icon: Compass, tone: "info" },
    { label: "Days covered", value: `${days}`, unit: "days", icon: Calendar, tone: "success" },
    { label: "Avg samples / day", value: `${Math.round(totalPoints / Math.max(1, days))}`, unit: "pts", icon: BarChart3, tone: "primary" },
  ] as const;

  const toneMap: Record<string, string> = {
    primary: "bg-brand-50 text-brand-500 dark:bg-brand-500/10 dark:text-brand-400",
    info: "bg-blue-50 text-blue-500 dark:bg-blue-500/10 dark:text-blue-400",
    success: "bg-green-50 text-green-500 dark:bg-green-500/10 dark:text-green-400",
    warning: "bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:text-orange-400",
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {stats.map((st) => (
        <div key={st.label} className="rounded-xl border border-gray-100 dark:border-gray-800 p-4 bg-white dark:bg-gray-900 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{st.label}</div>
            <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", toneMap[st.tone])}>
              <st.icon className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <div className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-white">{st.value}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{st.unit}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DailyTable({ rows }: { rows: any[] }) {
  const maxDist = Math.max(...rows.map((r) => parseFloat(r.distance || "0")), 0.01);

  const columns = [
    {
      name: 'Day',
      selector: (row: any) => row.date || row.receive_date?.slice(0, 10),
      sortable: true,
      cell: (row: any) => <div className="font-medium whitespace-nowrap text-gray-900 dark:text-white py-3">{row.date || row.receive_date?.slice(0, 10)}</div>,
    },
    {
      name: 'Distance',
      selector: (row: any) => parseFloat(row.distance || "0"),
      sortable: true,
      cell: (row: any) => {
        const dist = parseFloat(row.distance || "0");
        return (
          <div className="flex items-center gap-2 min-w-[140px] w-full">
            <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div className="h-full rounded-full bg-brand-500" style={{ width: `${(dist / maxDist) * 100}%` }} />
            </div>
            <span className="text-xs font-medium w-16 text-right text-gray-900 dark:text-white">{dist.toFixed(2)} nm</span>
          </div>
        );
      }
    },
    {
      name: 'Top Speed',
      selector: (row: any) => parseFloat(row.speed || "0"),
      sortable: true,
      cell: (row: any) => <div className="tabular-nums text-gray-900 dark:text-white">{Number(row.speed || "0").toFixed(2)} kn</div>
    },
    {
      name: 'Samples',
      selector: (row: any) => parseInt(row.count || "0", 10),
      sortable: true,
      cell: (row: any) => <div className="tabular-nums text-gray-900 dark:text-white">{row.count}</div>
    },
    {
      name: 'Activity',
      selector: (row: any) => {
        const count = parseInt(row.count || "0", 10);
        const countNotStopped = parseInt(row.countNotStopped || "0", 10);
        return count ? (countNotStopped / count) * 100 : 0;
      },
      sortable: true,
      cell: (row: any) => {
        const count = parseInt(row.count || "0", 10);
        const countNotStopped = parseInt(row.countNotStopped || "0", 10);
        const movePct = count ? (countNotStopped / count) * 100 : 0;
        return (
          <span
            className={cn(
              "inline-flex px-2 py-0.5 rounded-full text-xs font-medium",
              movePct === 0
                ? "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                : movePct < 30
                  ? "bg-orange-50 dark:bg-orange-500/10 text-orange-500 dark:text-orange-400"
                  : "bg-green-50 dark:bg-green-500/10 text-green-500 dark:text-green-400"
            )}
          >
            {movePct === 0 ? "Idle" : `${movePct.toFixed(0)}% moving`}
          </span>
        );
      }
    }
  ];

  return (
    <div className="hidden md:block [&_.rdt_TableCell]:!px-2 [&_.rdt_TableCol]:!px-2">
      <DatatableWithTheme
        columns={columns}
        data={rows}
        pagination
        paginationPerPage={10}
      />
    </div>
  );
}

function TimelineCards({ rows }: { rows: any[] }) {
  return (
    <div className="md:hidden -mx-5 px-5 space-y-3">
      {rows.map((r, idx) => {
        const heading = parseFloat(r.heading || "0");
        const speed = parseFloat(r.speed || "0");
        const dist = parseFloat(r.distance || "0");
        const m = r.vessel_history_meteorology;
        
        return (
          <div key={idx} className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] text-gray-500 dark:text-gray-400">{r.receive_date?.slice(0, 10)}</div>
                <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">{r.receive_date?.slice(11)}</div>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white text-[11px] shrink-0">
                <Compass className="h-3.5 w-3.5 text-brand-500" style={{ transform: `rotate(${heading}deg)` }} />
                {heading.toFixed(0)}°
              </span>
            </div>

            <div className="mt-3"><SpeedBar value={speed} /></div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-2.5">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <MapPin className="h-3 w-3" /> Position
                </div>
                <div className="text-xs font-semibold mt-1 tabular-nums text-gray-900 dark:text-white">{fmtCoord(r.latitude)}</div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">{fmtCoord(r.longitude)}</div>
              </div>
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-2.5">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <Navigation className="h-3 w-3" /> Distance
                </div>
                <div className="text-xs font-semibold mt-1 tabular-nums text-gray-900 dark:text-white">{dist.toFixed(2)} nm</div>
              </div>
              {m && (
                <>
                  <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-2.5">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <Cloud className="h-3 w-3" /> Weather
                    </div>
                    <div className="text-xs font-semibold mt-1 capitalize text-gray-900 dark:text-white">{m.summary}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                      <Thermometer className="h-3 w-3 text-orange-500" /> {parseFloat(m.temp || "0").toFixed(1)}°C
                    </div>
                  </div>
                  <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-2.5">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <Wind className="h-3 w-3" /> Wind
                    </div>
                    <div className="text-xs font-semibold mt-1 tabular-nums text-gray-900 dark:text-white">{parseFloat(m.wind_speed || "0").toFixed(1)} m/s</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
                      <Droplets className="h-3 w-3" /> {m.humidity}%
                      <Eye className="h-3 w-3 ml-1" /> {(parseFloat(m.visibility || "0") / 1000).toFixed(0)}km
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-start gap-1.5 text-xs">
              <MapPin className="h-3.5 w-3.5 mt-0.5 text-brand-500/70 shrink-0" />
              <div className="min-w-0">
                <div className="text-gray-900 dark:text-gray-200 leading-snug">{r.proximity}</div>
                {r.proximity_distance != null && (
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                    {r.proximity_distance.toFixed(0)} m · {r.proximity_direction?.trim()}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DailyCards({ rows }: { rows: any[] }) {
  const maxDist = Math.max(...rows.map((r) => parseFloat(r.distance || "0")), 0.01);
  return (
    <div className="md:hidden -mx-5 px-5 space-y-3 mt-3">
      {rows.map((d, idx) => {
        const dist = parseFloat(d.distance || "0");
        const count = parseInt(d.count || "0", 10);
        const countNotStopped = parseInt(d.countNotStopped || "0", 10);
        const movePct = count ? (countNotStopped / count) * 100 : 0;
        return (
          <div key={idx} className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{d.date || d.receive_date?.slice(0, 10)}</div>
              <span
                className={cn(
                  "inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0",
                  movePct === 0
                    ? "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                    : movePct < 30
                      ? "bg-orange-50 dark:bg-orange-500/10 text-orange-500 dark:text-orange-400"
                      : "bg-green-50 dark:bg-green-500/10 text-green-500 dark:text-green-400"
                )}
              >
                {movePct === 0 ? "Idle" : `${movePct.toFixed(0)}% moving`}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                <div className="h-full rounded-full bg-brand-500" style={{ width: `${(dist / maxDist) * 100}%` }} />
              </div>
              <span className="text-xs font-medium tabular-nums w-16 text-right text-gray-900 dark:text-white">{dist.toFixed(2)} nm</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Stat label="Top" value={`${Number(d.speed || "0").toFixed(1)} kn`} />
              <Stat label="Samples" value={`${count}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-xs font-semibold mt-1 tabular-nums text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}

function ChartView({ rows }: { rows: any[] }) {
  const speeds = rows.map((r) => parseFloat(r.speed || "0"));
  const max = Math.max(...speeds, 1);
  const labels = rows.map((r) => r.receive_date);

  const dataset = {
    labels: labels,
    datasets: [
      {
        label: 'Speed (Knots)',
        data: speeds,
        fill: true,
        backgroundColor: 'rgba(0, 150, 251, 0.1)',
        borderColor: '#0096FB',
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
      },
    ],
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">Speed over time</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Knots · {speeds.length} samples · peak {max.toFixed(2)} kn
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-4 bg-white dark:bg-gray-900 shadow-sm">
        {speeds.length > 0 ? (
          <LineChart title="Speed History" datasets={dataset} />
        ) : (
          <div className="flex items-center justify-center h-[240px] text-gray-400 text-sm">
            No data available for chart
          </div>
        )}
      </div>
    </div>
  );
}
